/*
 * Copyright (c) 2016 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */


import {Component, ContentChildren, ElementRef, HostListener, Input, QueryList} from '@angular/core';

import {Point} from '../../popover/common/popover';
import {menuPositions} from '../../popover/dropdown/menu-positions';
import {ButtonInGroupService} from '../providers/buttonInGroup.service';

import {Button} from './button';
export class ButtonGroup {
     buttons: QueryList<Button>;
/**
 * @param {?} buttonGroupNewService
 * @param {?} elementRef
 */
constructor(public buttonGroupNewService: ButtonInGroupService,
private elementRef: ElementRef) {}

    inlineButtons: Button[] = [];
    menuButtons: Button[] = [];
/**
 * 1. Initializes the initial Button Group View
 * 2. Subscribes to changes on the ContentChildren
 *    in case the user content projection changes
 * @return {?}
 */
ngAfterContentInit() {
        this.initializeButtons();
        this.buttonGroupNewService.changes.subscribe(button => this.rearrangeButton(button));
        this.buttons.changes.subscribe(() => {
            this.initializeButtons();
        });
    }
/**
 * Moves the button into the other ViewContainer
 * when an update is received.
 * 
 * @param {?} button
 * @return {?}
 */
rearrangeButton(button: Button): void {
        let /** @type {?} */ fromView: Button[];
        let /** @type {?} */ toView: Button[];
        if (button.inMenu) {
            fromView = this.inlineButtons;
            toView = this.menuButtons;
        } else {
            fromView = this.menuButtons;
            toView = this.inlineButtons;
        }
        const /** @type {?} */ index: number = fromView.indexOf(button);
        if (index > -1) {
            fromView.splice(index, 1);
            const /** @type {?} */ moveIndex = this.getMoveIndex(button);
            if (moveIndex <= toView.length) {
                toView.splice(moveIndex, 0, button);
            }
        }
    }
/**
 * Author: Eudes
 * 
 * Finds the order of a button w.r.t other buttons
 * 
 * @param {?} buttonToMove
 * @return {?}
 */
getMoveIndex(buttonToMove: Button): number {
        const /** @type {?} */ tempArr: Button[] = this.buttons.filter(button => (button.inMenu === buttonToMove.inMenu));
        return tempArr.indexOf(buttonToMove);
    }
/**
 * Finds where each button belongs based on
 * the ContentChildren
 * @return {?}
 */
initializeButtons(): void {
        const /** @type {?} */ tempInlineButtons: Button[] = [];
        const /** @type {?} */ tempInMenuButtons: Button[] = [];
        this.buttons.forEach((button) => {
            if (button.inMenu) {
                tempInMenuButtons.push(button);
            } else {
                tempInlineButtons.push(button);
            }
        });
        this.inlineButtons = tempInlineButtons;
        this.menuButtons = tempInMenuButtons;
    }
private _menuPosition: string;
/**
 * @return {?}
 */
get menuPosition(): string {
        return this._menuPosition;
    }
/**
 * @param {?} pos
 * @return {?}
 */
set menuPosition(pos: string) {
        if (pos && (menuPositions.indexOf(pos) > -1)) {
            this._menuPosition = pos;
        } else {
            this._menuPosition = "bottom-left";
        }
        // set the popover values based on menu position
        switch (this._menuPosition) {
            case ("top-right"):
                this.anchorPoint = Point.TOP_RIGHT;
                this.popoverPoint = Point.RIGHT_BOTTOM;
                break;
            case ("top-left"):
                this.anchorPoint = Point.TOP_LEFT;
                this.popoverPoint = Point.LEFT_BOTTOM;
                break;
            case ("bottom-right"):
                this.anchorPoint = Point.BOTTOM_RIGHT;
                this.popoverPoint = Point.RIGHT_TOP;
                break;
            case ("bottom-left"):
                this.anchorPoint = Point.BOTTOM_LEFT;
                this.popoverPoint = Point.LEFT_TOP;
                break;
            case ("right-top"):
                this.anchorPoint = Point.RIGHT_TOP;
                this.popoverPoint = Point.LEFT_TOP;
                break;
            case ("right-bottom"):
                this.anchorPoint = Point.RIGHT_BOTTOM;
                this.popoverPoint = Point.LEFT_BOTTOM;
                break;
            case ("left-top"):
                this.anchorPoint = Point.LEFT_TOP;
                this.popoverPoint = Point.RIGHT_TOP;
                break;
            case ("left-bottom"):
                this.anchorPoint = Point.LEFT_BOTTOM;
                this.popoverPoint = Point.RIGHT_BOTTOM;
                break;
            default:
                this.anchorPoint = Point.BOTTOM_LEFT;
                this.popoverPoint = Point.LEFT_TOP;
                break;
        }
    }
private _openMenu: boolean = false;
/**
 * @return {?}
 */
get openMenu(): boolean {
        return this._openMenu;
    }
/**
 * @param {?} value
 * @return {?}
 */
set openMenu(value: boolean) {
        this._openMenu = value;
    }
public anchorPoint: Point = Point.BOTTOM_LEFT;
public popoverPoint: Point = Point.LEFT_TOP;
/**
 * Toggle the Dropdown Menu when the Dropdown Toggle is
 * clicked. Also set a flag that indicates that the toggle
 * was clicked so that we don't traverse the DOM to find the
 * location of the click.
 * @return {?}
 */
toggleMenu(): void {
        this.openMenu = !this.openMenu;
        this._overflowMenuToggleClicked = true;
    }
/**
 * Flag with indicates if the overflow menu toggle was clicked.
 * If true, this can save us traversing the DOM to find
 * whether the click was withing the button group toggle
 * or menu in the onMouseClick method
 */
private _overflowMenuToggleClicked: boolean = false;
/**
 * Called on mouse clicks anywhere in the DOM.
 * Checks to see if the mouseclick happened on the host or outside
 * @param {?} target
 * @return {?}
 */
onMouseClick(target: any): void {
        if (this.openMenu && !this._overflowMenuToggleClicked) {
            // Reset the overflow menu toggle clicked flag
            this._overflowMenuToggleClicked = false;
            let /** @type {?} */ current: any = target;  // Get the element in the DOM on which the mouse was clicked
            const /** @type {?} */ host: any = this.elementRef.nativeElement;  // Current Button Group

            if (current.classList.contains("dropdown-menu")) {
                current = current.parentNode;
                while (current) {
                    if (current === document) {
                        this.openMenu = false;
                        return;
                    }

                    // If clicked on dropdown menu and menu is in host
                    // do nothing
                    if (current === host) {
                        return;
                    }
                    current = current.parentNode;
                }
            }
            this.openMenu = false;
        }
        this._overflowMenuToggleClicked = false;  // Reset the overflow menu toggle clicked flag
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-button-group",
    template: `
      <ng-container *ngFor="let inlineButton of inlineButtons">
          <ng-template [ngTemplateOutlet]="inlineButton.templateRef"></ng-template>
      </ng-container>
      <ng-container *ngIf="menuButtons.length > 0">
          <div
              class="btn-group-overflow open"
              [ngClass]="menuPosition"
              #anchor>
              <button
                  class="btn dropdown-toggle"
                  (click)="toggleMenu()">
                  <clr-icon shape="ellipsis-horizontal"></clr-icon>
              </button>
              <div
                  class="dropdown-menu"
                  *clrPopoverOld="openMenu; anchor: anchor; anchorPoint: anchorPoint; popoverPoint: popoverPoint;">
                  <ng-template [ngTemplateOutlet]="ref"></ng-template>
              </div>
          </div>
      </ng-container>
      <ng-template #ref>
          <ng-container *ngFor="let menuButton of menuButtons">
              <ng-template [ngTemplateOutlet]="menuButton.templateRef"></ng-template>
          </ng-container>
      </ng-template>
    `,
    providers: [ButtonInGroupService],
    host: {"[class.btn-group]": "true"}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ButtonInGroupService, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'buttons': [{ type: ContentChildren, args: [Button, ] },],
'menuPosition': [{ type: Input, args: ["clrMenuPosition", ] },],
'onMouseClick': [{ type: HostListener, args: ["document:click", ["$event.target"], ] },],
};
}

function ButtonGroup_tsickle_Closure_declarations() {
/** @type {?} */
ButtonGroup.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ButtonGroup.ctorParameters;
/** @type {?} */
ButtonGroup.propDecorators;
/** @type {?} */
ButtonGroup.prototype.buttons;
/** @type {?} */
ButtonGroup.prototype.inlineButtons;
/** @type {?} */
ButtonGroup.prototype.menuButtons;
/** @type {?} */
ButtonGroup.prototype._menuPosition;
/** @type {?} */
ButtonGroup.prototype._openMenu;
/** @type {?} */
ButtonGroup.prototype.anchorPoint;
/** @type {?} */
ButtonGroup.prototype.popoverPoint;
/**
 * Flag with indicates if the overflow menu toggle was clicked.
 * If true, this can save us traversing the DOM to find
 * whether the click was withing the button group toggle
 * or menu in the onMouseClick method
 * @type {?}
 */
ButtonGroup.prototype._overflowMenuToggleClicked;
/** @type {?} */
ButtonGroup.prototype.buttonGroupNewService;
/** @type {?} */
ButtonGroup.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
